import React, { useState, useEffect } from 'react'
import './FilterPanel.css'
import { Search, Filter, X, ChevronDown, ChevronUp } from 'lucide-react'

const FilterPanel = ({ filters, data, onFilterChange }) => {
  const [sectors, setSectors] = useState([])
  const [industries, setIndustries] = useState([])
  const [expandedSections, setExpandedSections] = useState({
    search: true,
    sectors: true,
    industries: true,
    metrics: true
  })

  useEffect(() => {
    if (data.length > 0) {
      const uniqueSectors = [...new Set(data.map(item => item.Sector).filter(Boolean))].sort()
      const uniqueIndustries = [...new Set(data.map(item => item.Industry).filter(Boolean))].sort()
      setSectors(uniqueSectors)
      setIndustries(uniqueIndustries)
    }
  }, [data])

  const handleSearchChange = (value) => {
    onFilterChange({ searchTerm: value })
  }

  const handleSectorChange = (sector, checked) => {
    const newSectors = checked 
      ? [...filters.sectors, sector]
      : filters.sectors.filter(s => s !== sector)
    onFilterChange({ sectors: newSectors })
  }

  const handleIndustryChange = (industry, checked) => {
    const newIndustries = checked 
      ? [...filters.industries, industry]
      : filters.industries.filter(i => i !== industry)
    onFilterChange({ industries: newIndustries })
  }

  const handleMarketCapChange = (min, max) => {
    onFilterChange({ 
      marketCapRange: [min * 1000000000, max * 1000000000] 
    })
  }

  const handlePERangeChange = (min, max) => {
    onFilterChange({ peRange: [min, max] })
  }

  const handleDividendYieldChange = (min, max) => {
    onFilterChange({ dividendYieldRange: [min, max] })
  }

  const clearAllFilters = () => {
    onFilterChange({
      sectors: [],
      industries: [],
      marketCapRange: [0, 1000000000000],
      peRange: [0, 100],
      dividendYieldRange: [0, 20],
      searchTerm: ''
    })
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const formatMarketCap = (value) => {
    return `${value}B`
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.searchTerm) count++
    if (filters.sectors.length > 0) count++
    if (filters.industries.length > 0) count++
    if (filters.marketCapRange[0] > 0 || filters.marketCapRange[1] < 1000000000000) count++
    if (filters.peRange[0] > 0 || filters.peRange[1] < 100) count++
    if (filters.dividendYieldRange[0] > 0 || filters.dividendYieldRange[1] < 20) count++
    return count
  }

  return (
    <div className="filter-panel">
      <div className="filter-header">
        <Filter className="filter-icon" />
        <h3>Filters</h3>
        {getActiveFiltersCount() > 0 && (
          <button className="clear-filters" onClick={clearAllFilters}>
            <X className="clear-icon" />
            Clear All
          </button>
        )}
      </div>

      {/* Search Filter */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('search')}>
          <Search className="section-icon" />
          <span>Search</span>
          {expandedSections.search ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.search && (
          <div className="section-content">
            <input
              type="text"
              placeholder="Search companies or tickers..."
              value={filters.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="search-input"
            />
          </div>
        )}
      </div>

      {/* Sectors Filter */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('sectors')}>
          <Filter className="section-icon" />
          <span>Sectors ({filters.sectors.length})</span>
          {expandedSections.sectors ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.sectors && (
          <div className="section-content">
            <div className="checkbox-list">
              {sectors.map(sector => (
                <label key={sector} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.sectors.includes(sector)}
                    onChange={(e) => handleSectorChange(sector, e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{sector}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Industries Filter */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('industries')}>
          <Filter className="section-icon" />
          <span>Industries ({filters.industries.length})</span>
          {expandedSections.industries ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.industries && (
          <div className="section-content">
            <div className="checkbox-list">
              {industries.map(industry => (
                <label key={industry} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.industries.includes(industry)}
                    onChange={(e) => handleIndustryChange(industry, e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">{industry}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Metrics Filter */}
      <div className="filter-section">
        <div className="section-header" onClick={() => toggleSection('metrics')}>
          <Filter className="section-icon" />
          <span>Financial Metrics</span>
          {expandedSections.metrics ? <ChevronUp /> : <ChevronDown />}
        </div>
        {expandedSections.metrics && (
          <div className="section-content">
            {/* Market Cap Range */}
            <div className="range-filter">
              <label>Market Cap (Billions)</label>
              <div className="range-inputs">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.marketCapRange[0] / 1000000000}
                  onChange={(e) => handleMarketCapChange(parseFloat(e.target.value) || 0, filters.marketCapRange[1] / 1000000000)}
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.marketCapRange[1] / 1000000000}
                  onChange={(e) => handleMarketCapChange(filters.marketCapRange[0] / 1000000000, parseFloat(e.target.value) || 1000)}
                />
              </div>
            </div>

            {/* P/E Range */}
            <div className="range-filter">
              <label>P/E Ratio</label>
              <div className="range-inputs">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.peRange[0]}
                  onChange={(e) => handlePERangeChange(parseFloat(e.target.value) || 0, filters.peRange[1])}
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.peRange[1]}
                  onChange={(e) => handlePERangeChange(filters.peRange[0], parseFloat(e.target.value) || 100)}
                />
              </div>
            </div>

            {/* Dividend Yield Range */}
            <div className="range-filter">
              <label>Dividend Yield (%)</label>
              <div className="range-inputs">
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.dividendYieldRange[0]}
                  onChange={(e) => handleDividendYieldChange(parseFloat(e.target.value) || 0, filters.dividendYieldRange[1])}
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.dividendYieldRange[1]}
                  onChange={(e) => handleDividendYieldChange(filters.dividendYieldRange[0], parseFloat(e.target.value) || 20)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FilterPanel 